import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { OrderChainPanel } from "../../components/OrderChain";
import { Card, ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { QUEUE_BUCKETS, useCreditProposals, useOrderChain, useOrderQueue, useSetCreditLimit, type QueueBucket } from "../../lib/api";
import { fmtNumber, fmtValue, localeFor } from "../../lib/format";

/** Finance's work queue on the orders tab (OS-0672): the orders *held for credit*, *to deliver*, *to invoice*, *to collect*, and the
 * ones Finance *rejected*, searchable by order number, customer or customer reference; a row opens the order's trail, where the
 * next step is taken. Sales read the same queue. Which queue an order is in is the rule's (engines/orders.queue_buckets). The
 * credit limits sales proposed wait here for Finance too. */
export function WorkQueueTab() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const [params, setParams] = useSearchParams();
  const bucket: QueueBucket = (QUEUE_BUCKETS as readonly string[]).includes(params.get("bucket") ?? "") ? (params.get("bucket") as QueueBucket) : "held";
  const order = params.get("order");
  const [search, setSearch] = useState("");
  const deferred = useDeferredValue(search.trim());
  const q = useOrderQueue(bucket, deferred);
  const selected = useOrderChain(order ?? undefined);
  const put = (changes: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    p.set("tab", "queue");
    for (const [k, v] of Object.entries(changes)) { if (v == null) p.delete(k); else p.set(k, v); }
    setParams(p);
  };
  const fmtDate = (s: string | null | undefined) => (s ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium" }).format(new Date(`${s.slice(0, 10)}T00:00:00`)) : "–");
  return (
    <div className="mt-4 space-y-4" data-o2c-queue={bucket}>
      <Card className="px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-base font-semibold">{t("o2c.queue.title")}</h2>
            <p className="max-w-3xl text-xs text-brand-grey">{t("o2c.queue.hint")}</p>
          </div>
          {q.data && <FreshnessStamp lastUpdated={q.data.as_of} />}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1" role="tablist">
            {QUEUE_BUCKETS.map((b) => (
              <button key={b} role="tab" aria-selected={bucket === b} onClick={() => put({ bucket: b, order: null })} data-o2c-bucket={b}
                className={`rounded px-3 py-1 text-sm ${bucket === b ? "bg-brand-primary font-semibold text-white" : "border border-brand-grey-light text-brand-charcoal hover:bg-brand-grey-light/40"}`}>
                {t(`o2c.queue.bucket.${b}`)} <span className={`ml-1 rounded px-1.5 text-[11px] ${bucket === b ? "bg-white/20" : "bg-brand-grey-light"}`}>{q.data?.counts[b] ?? "…"}</span>
              </button>
            ))}
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("o2c.queue.search")} className="w-72 rounded border border-brand-grey-light px-2 py-1 text-sm" data-o2c-queue-search />
        </div>
      </Card>

      {order && (selected.data ? <div data-o2c-queue-selected={order}><OrderChainPanel chain={selected.data} /></div> : selected.isError ? <ErrorBox error={selected.error} /> : <Skeleton className="h-32" />)}

      {q.isError && <ErrorBox error={q.error} onRetry={() => void q.refetch()} />}
      {!q.data && !q.isError && <Skeleton className="h-40" />}
      {q.data && (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-grey-light/40 text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr><th className="px-3 py-2">{t("o2c.queue.col.order")}</th><th className="px-3 py-2">{t("o2c.customer")}</th><th className="px-3 py-2">{t("o2c.queue.col.date")}</th>
                <th className="px-3 py-2 text-right">{t("o2c.amountCol")}</th><th className="px-3 py-2 text-right">{t("o2c.delivered")}</th><th className="px-3 py-2 text-right">{t("o2c.billed")}</th>
                <th className="px-3 py-2 text-right">{t("o2c.outstanding")}</th><th className="px-3 py-2">{t("o2c.queue.col.why")}</th></tr>
            </thead>
            <tbody className="divide-y divide-brand-grey-light">
              {q.data.rows.map((r) => (
                <tr key={r.name} onClick={() => put({ order: r.name })} className={`cursor-pointer hover:bg-brand-grey-light/30 ${order === r.name ? "bg-blue-50/40" : ""}`} data-o2c-queue-row={r.name}>
                  <td className="px-3 py-2 font-mono text-xs">{r.name}{r.po_no && <div className="font-sans text-brand-grey">{r.po_no}</div>}</td>
                  <td className="px-3 py-2"><div className="font-semibold">{r.customer_name || r.customer}</div><div className="text-xs text-brand-grey">{r.company}</div></td>
                  <td className="px-3 py-2 text-xs">{fmtDate(r.transaction_date)}{r.delivery_date && <div className="text-brand-grey">→ {fmtDate(r.delivery_date)}</div>}</td>
                  <td className="px-3 py-2 text-right">{fmtValue(r.grand_total, r.currency || "CDF", lng)}</td>
                  <td className="px-3 py-2 text-right">{fmtNumber(r.per_delivered, lng, 0)} %</td>
                  <td className="px-3 py-2 text-right">{fmtNumber(r.per_billed, lng, 0)} %</td>
                  <td className="px-3 py-2 text-right">{r.outstanding > 0 ? <span className="text-status-amber">{fmtValue(r.outstanding, r.currency || "CDF", lng)}</span> : "–"}</td>
                  <td className="max-w-xs px-3 py-2 text-xs text-brand-grey">{bucket === "rejected" ? r.os_rejection_reason : bucket === "held" ? r.hold_reason : t(`o2c.status.${r.status}`, r.status ?? "")}</td>
                </tr>
              ))}
              {q.data.rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-brand-grey">{t("o2c.queue.empty")}</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
      <CreditProposals />
    </div>
  );
}

/** Credit limits sales proposed on new customers (OS-0671): shown to everybody who reads the queue, set by Finance only. */
function CreditProposals() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useCreditProposals();
  const set = useSetCreditLimit();
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  // the decision stays on screen once the row has gone: otherwise setting the last proposal empties the card silently
  const [done, setDone] = useState<{ customer: string; company: string; amount: number } | null>(null);
  if (!q.data || (q.data.rows.length === 0 && !done)) return null;
  return (
    <Card className="overflow-x-auto" data-o2c-credit-proposals>
      <div className="border-b border-brand-grey-light px-4 py-2">
        <h3 className="font-heading text-base font-semibold">{t("o2c.queue.proposals")}</h3>
        <p className="text-xs text-brand-grey">{t("o2c.queue.proposalsHint")}</p>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="px-3 py-2">{t("o2c.customer")}</th><th className="px-3 py-2">{t("o2c.company")}</th>
          <th className="px-3 py-2 text-right">{t("o2c.queue.proposed")}</th><th className="px-3 py-2 text-right">{t("o2c.credit.limit")}</th><th className="px-3 py-2">{t("o2c.queue.proposedBy")}</th><th className="px-3 py-2" /></tr></thead>
        <tbody className="divide-y divide-brand-grey-light">
          {q.data.rows.map((r) => (
            <tr key={`${r.name}-${r.company}`} data-o2c-proposal={r.name}>
              <td className="px-3 py-2 font-semibold">{r.customer_name || r.name}</td><td className="px-3 py-2 text-xs">{r.company}</td>
              <td className="px-3 py-2 text-right">{fmtValue(r.proposed, "CDF", lng)}</td><td className="px-3 py-2 text-right">{r.current != null ? fmtValue(r.current, "CDF", lng) : "–"}</td>
              <td className="px-3 py-2 text-xs text-brand-grey">{r.proposed_by}{r.proposed_on ? ` · ${r.proposed_on}` : ""}</td>
              <td className="px-3 py-2">
                {q.data!.may_set_credit ? (
                  <div className="flex items-center justify-end gap-1">
                    <input type="number" value={amounts[r.name] ?? String(r.proposed)} onChange={(e) => setAmounts({ ...amounts, [r.name]: e.target.value })} className="w-36 rounded border border-brand-grey-light px-2 py-0.5 text-xs" data-o2c-proposal-amount />
                    <button disabled={set.isPending || !(Number(amounts[r.name] ?? r.proposed) >= 0)} onClick={() => { const amount = Number(amounts[r.name] ?? r.proposed); set.mutate({ customer: r.name, company: r.company, credit_limit: amount }, { onSuccess: () => setDone({ customer: r.customer_name || r.name, company: r.company, amount }) }); }}
                      className="rounded bg-brand-primary px-2 py-0.5 text-xs font-semibold text-white disabled:opacity-50" data-o2c-proposal-set>{t("o2c.queue.setLimit")}</button>
                  </div>
                ) : <span className="text-xs text-brand-grey">{t("o2c.queue.financeDecides")}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {done && <p className="border-t border-brand-grey-light px-4 py-2 text-sm text-status-green" data-o2c-proposal-done>✓ {t("o2c.queue.limitSet", { customer: done.customer, company: done.company, amount: fmtValue(done.amount, "CDF", lng) })}</p>}
      {set.isError && <div className="p-3"><ErrorBox error={set.error} /></div>}
    </Card>
  );
}
